import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, CircleNotch } from 'lucide-react';
import { cn } from '@/lib/utils';

type LoadingSize = 'sm' | 'md' | 'lg';
type LoadingVariant = 'spinner' | 'dots' | 'pulse';

interface LoadingProps {
  size?: LoadingSize;
  variant?: LoadingVariant;
  className?: string;
  fullScreen?: boolean;
  text?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
};

const Loading = ({
  size = 'md',
  variant = 'spinner',
  className,
  fullScreen = false,
  text,
}: LoadingProps) => {
  const containerClasses = cn(
    'flex items-center justify-center',
    fullScreen && 'fixed inset-0 bg-background/80 backdrop-blur-sm z-50',
    className
  );

  const spinnerVariants = {
    spinner: <Loader2 className={cn('animate-spin', sizeClasses[size])} />,
    dots: (
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={cn('rounded-full bg-primary', sizeClasses[size])}
            animate={{
              y: ['0%', '-50%', '0%'],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>
    ),
    pulse: (
      <motion.div
        className={cn('rounded-full bg-primary', sizeClasses[size])}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [1, 0.5, 1],
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
        }}
      />
    ),
  };

  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center gap-2">
        {spinnerVariants[variant]}
        {text && <p className="text-sm text-muted-foreground animate-pulse">{text}</p>}
      </div>
    </div>
  );
};

export { Loading };
